import React, { useState } from 'react';
import { Avatar, Tooltip, Spin } from 'antd';
import { PhotoIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

/**
 * ProductThumbnail Component
 * Displays a 32x32 thumbnail image with fallback options
 */
const ProductThumbnail = ({ 
  thumbnailUrl, 
  originalImageUrl, 
  productName = 'Product',
  size = 32,
  showTooltip = true,
  onClick = null,
  className = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(thumbnailUrl || originalImageUrl);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
    
    // If thumbnail failed and we have original URL, try that
    if (currentUrl === thumbnailUrl && originalImageUrl && originalImageUrl !== thumbnailUrl) {
      console.log('Thumbnail failed, trying original image:', originalImageUrl);
      setCurrentUrl(originalImageUrl);
      setError(false);
      setLoading(true);
    }
  };

  const handleImageLoadStart = () => {
    setLoading(true);
  };

  // Fallback icon when no image is available
  const fallbackIcon = (
    <Avatar
      size={size}
      icon={<PhotoIcon className="w-4 h-4" />}
      style={{
        backgroundColor: '#f5f5f5',
        color: '#8c8c8c',
        border: '1px solid #d9d9d9'
      }}
    />
  );

  // Error icon when image fails to load
  const errorIcon = (
    <Avatar 
      size={size} 
      icon={<ExclamationCircleIcon className="w-4 h-4" />}
      style={{ 
        backgroundColor: '#fff2f0', 
        color: '#ff4d4f',
        border: '1px solid #ffccc7'
      }}
    />
  );

  // Loading spinner
  const loadingSpinner = (
    <Avatar 
      size={size} 
      icon={<Spin size="small" />}
      style={{ 
        backgroundColor: '#f5f5f5', 
        border: '1px solid #d9d9d9'
      }}
    />
  );

  // Main image component
  const imageComponent = () => {
    if (!currentUrl) {
      return fallbackIcon;
    }

    if (loading) {
      return loadingSpinner;
    }

    if (error) {
      return errorIcon;
    }

    // For larger sizes (>50px), use regular img instead of Avatar to avoid circular shape
    if (size > 50) {
      return (
        <img
          src={currentUrl}
          alt={`${productName} thumbnail`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          onLoadStart={handleImageLoadStart}
          style={{
            width: size,
            height: size,
            objectFit: 'cover',
            border: '1px solid #d9d9d9',
            borderRadius: 0,
            cursor: onClick ? 'pointer' : 'default'
          }}
          onClick={onClick}
        />
      );
    }

    return (
      <Avatar
        size={size}
        src={
          <img
            src={currentUrl}
            alt={`${productName} thumbnail`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onLoadStart={handleImageLoadStart}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        }
        style={{
          border: '1px solid #d9d9d9',
          cursor: onClick ? 'pointer' : 'default'
        }}
        onClick={onClick}
      />
    );
  };

  const thumbnail = imageComponent();

  // Wrap with tooltip if enabled
  if (showTooltip) {
    const tooltipTitle = error 
      ? 'Failed to load image' 
      : loading 
        ? 'Loading image...' 
        : `${productName} - Click to view full image`;

    return (
      <Tooltip title={tooltipTitle} placement="top">
        <div className={className}>
          {thumbnail}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={className}>
      {thumbnail}
    </div>
  );
};

export default ProductThumbnail;
